import { MyColumn, MyDataTable } from '@interstellar-component'
import AuthImage from '@src/components/AuthImage'
import { useDashboard } from '../Context'

function getBadgeColor(kol) {
  switch (kol) {
    case 'KOL 1':
      return 'bg-success/50 text-success/700 border border-success/200'
    case 'KOL 2':
      return 'bg-warning/50 text-warning/700 border border-warning/200'
    case 'KOL 3':
      return 'bg-warning/100 text-warning/800 border border-warning/300'
    case 'KOL 4':
      return 'bg-error/50 text-error/700 border border-error/200'
    case 'KOL 5':
      return 'bg-error/100 text-error/800 border border-error/300'
    default:
      return 'bg-gray-100 text-gray-700 border border-gray-200'
  }
}

function formatRupiah(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return `Rp${n.toLocaleString('id-ID')}`
}

function formatDate(value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function initialsOf(name) {
  if (!name) return '??'
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('')
}

function Avatar({ src, name }) {
  if (src) {
    return (
      <AuthImage
        src={src}
        alt={name || ''}
        className="h-10 w-10 rounded-full object-cover"
      />
    )
  }
  return (
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand/50 text-sm font-semibold text-brand/700">
      {initialsOf(name)}
    </span>
  )
}

function EmployeeTable() {
  const {
    employees,
    employeesMeta,
    sortField,
    sortOrder,
    handleSort,
    handleSelectionChange,
    handlePageChange,
    page,
    limit,
  } = useDashboard()

  const values = {
    data: employees || [],
    meta: {
      current_page: employeesMeta?.current_page ?? page,
      next_page: employeesMeta?.next_page ?? null,
      prev_page: employeesMeta?.prev_page ?? null,
      per_page: limit,
      total: employeesMeta?.total ?? 0,
      total_page: employeesMeta?.total_page ?? 1,
    },
    checkedAll: (employees || []).every((d) => d.checked),
  }

  return (
    <div className="px-8 pb-8">
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <MyDataTable
          values={values}
          selectionMode="multiple"
          onSelectionChange={handleSelectionChange}
          paginator
          currentSortFieldFromParams={sortField}
          currentSortOrderFromParams={sortOrder}
          onChangePagination={(nextPage) => handlePageChange(nextPage)}
        >
          <MyColumn
            header="Name"
            field="name"
            onSort={handleSort}
            body={(row) => (
              <div className="flex items-center gap-3 pr-8">
                <Avatar src={row.avatar_url} name={row.name} />
                <div className="flex flex-col">
                  <span className="whitespace-nowrap text-sm font-medium text-gray-900">
                    {row.name}
                  </span>
                  <span className="whitespace-nowrap text-sm text-gray-500">
                    {[row.workforce_code, row.position].filter(Boolean).join(' • ') || '—'}
                  </span>
                </div>
              </div>
            )}
          />
          <MyColumn
            header="Level"
            field="level"
            onSort={handleSort}
            body={(row) => <span className="text-sm text-gray-600">{row.level || '—'}</span>}
          />
          <MyColumn
            header="Collectability"
            field="kolektibilitas"
            onSort={handleSort}
            body={(row) => (
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getBadgeColor(
                  row.kolektibilitas,
                )}`}
              >
                {row.kolektibilitas || '—'}
              </span>
            )}
          />
          <MyColumn
            header="Credit score"
            field="credit_score"
            onSort={handleSort}
            body={(row) => (
              <span className="text-sm text-gray-600">{row.credit_score ?? '—'}</span>
            )}
          />
          <MyColumn
            header="Footprint/3 mo."
            field="footprint"
            onSort={handleSort}
            body={(row) => (
              <span className="text-sm text-gray-600">{row.footprint_3m ?? 0}</span>
            )}
          />
          <MyColumn
            header="Outstanding"
            field="outstanding"
            onSort={handleSort}
            body={(row) => (
              <span className="text-sm text-gray-600">{formatRupiah(row.outstanding)}</span>
            )}
          />
          <MyColumn
            header="Data as"
            field="data_as"
            onSort={handleSort}
            body={(row) => (
              <span className="whitespace-nowrap text-sm text-gray-600">
                {formatDate(row.data_as)}
              </span>
            )}
          />
        </MyDataTable>
      </div>
    </div>
  )
}

export default EmployeeTable
